import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { dica, rotulo } from '../ui/dica';
import { api } from '../api';
import { ApiError, type Profile, type ProfileSkill, type SkillLock } from '../api/types';
import { existe, idiomaAtual, numero, t, umaCasa } from '../i18n/idioma';
import { nomeDaMagia, nomeDaNotoriedade, nomeDaSkill } from '../i18n/nomes';
import { ehMagia } from '../data/magias';
import { CoroaVip } from './CoroaVip';
import { SeloFundador } from './SeloFundador';
import { nickFillFromLabel } from '../look/nickColor';
import { FADIGA_TETO, fadigaPct } from '../world/fadiga';
import { stones } from './itemUi';
import { iconePara } from '../assets/items';
import { useBuffs } from '../world/buffs';
import { IcAr, IcDano, IcMagia, IcMana, IcVel, IcVida, IcVigor } from './icones/IconesV3';
import { Icone } from './kit/Icone';
import type { GanhoDoPerfil } from '../realtime/cityHub';
import type { GanhosDoPerfil } from '../world/useWorld';

interface Props {
  active: boolean;
  /** GM ou ADMIN. Vazio no jogador. */
  marca?: '' | 'GM' | 'ADMIN';
  vipAte?: number;
  tick: number;
  /** Os ganhos de skill, atributo e barra de XP (avisos 99, 167 e 255), aplicados na hora com o perfil aberto. */
  ganho?: GanhosDoPerfil | null;
  canvas: ReactNode;
  onChanged(text: string): void;
}

/* Furtividade saiu do jogo por enquanto (SkillIds.ForaDoJogo no servidor): sem a aba. */
const GROUP_ORDER = ['combate', 'magia', 'profissao'] as const;
const GROUP_CHAVE: Record<(typeof GROUP_ORDER)[number], string> = {
  combate: 'perfil.grupoCombate',
  magia: 'perfil.grupoMagia',
  profissao: 'perfil.grupoProfissao'
};

const LOCK_CYCLE: Record<SkillLock, SkillLock> = {
  raise: 'locked',
  locked: 'lower',
  lower: 'raise'
};

const LOCK_ICON: Record<SkillLock, string> = {
  raise: '▲',
  locked: '■',
  lower: '▼'
};

const LOCK_CHAVE: Record<SkillLock, string> = {
  raise: 'perfil.travaSubir',
  locked: 'perfil.travaFixa',
  lower: 'perfil.travaDescer'
};

function lockLine(skill: ProfileSkill, next: SkillLock): string {
  const nome = nomeDaSkill(skill.id, skill.name);
  if (next === 'locked') return t('perfil.travaSkill', { skill: nome });
  if (next === 'raise') return t('perfil.destravaSkill', { skill: nome });
  return t('perfil.skillPodeDescer', { skill: nome });
}

function oneDecimal(n: number): string {
  return n.toLocaleString(idiomaAtual(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** O progresso entre parênteses: "+0,2", "−0,3". Sem progresso (menos de 0,05), nada. */
function textoGanho(n: number | undefined): string | null {
  if (n == null || Math.abs(n) < 0.05) return null;
  return `${n > 0 ? '+' : '−'}${oneDecimal(Math.abs(n))}`;
}

function fmtDelta(n: number): string {
  if (n > 0) return `+${numero(n)}`;
  return numero(n);
}

function setBp(familia: string, completo: boolean): number {
  if (!completo) return 0;
  if (familia === 'nenhuma') return 500;
  if (familia === 'studded') return 1500;
  if (familia === 'bone') return 3000;
  return 0;
}

function nomeFamilia(familia: string): string {
  const chave = `perfil.familia.${familia}`;
  const nome = t(chave);
  return nome === chave ? familia : nome;
}

/** 1 vira 100%. 1,15 vira 115%. Fração fica com uma casa. */
function textoVelocidade(mult: number) {
  const pct = Math.round(mult * 1000) / 10;
  const inteiro = Math.abs(pct - Math.round(pct)) < 0.05;
  return `${inteiro ? numero(Math.round(pct)) : umaCasa(pct)}%`;
}

const STAT: Readonly<Record<string, ['strength' | 'dexterity' | 'intelligence', 'strengthBase' | 'dexterityBase' | 'intelligenceBase']>> = {
  str: ['strength', 'strengthBase'],
  dex: ['dexterity', 'dexterityBase'],
  int: ['intelligence', 'intelligenceBase']
};

/**
 * A ficha depois de um ganho: a skill soma o que subiu (o total e o teto de 100 juntos); o atributo
 * vira o valor novo (o efetivo sobe o mesmo tanto, porque o bônus de item não muda).
 */
export function aplicarGanho(p: Profile, g: GanhoDoPerfil): Profile {
  if (g.tipo === 'xp') {
    // A barra de XP (techspec-xp-skills §05.2): a de agora e o custo do próximo +0,1.
    const por = new Map((g.barras ?? []).map(b => [b.id, b]));
    if (!por.size) return p;
    return { ...p, skills: p.skills.map(s => { const b = por.get(s.id); return b ? { ...s, xp: b.xp, custo: b.custo } : s; }) };
  }
  if (!Number.isFinite(g.valor)) return p;
  if (g.tipo === 'skill') {
    let delta = 0;
    const skills = p.skills.map(s => {
      if (s.id !== g.id) return s;
      const novo = Math.min(100, Math.round((s.value + g.valor) * 10) / 10);
      delta = novo - s.value;
      // O progresso desde a última limpeza sobe junto (techspec-progresso-skills).
      return { ...s, value: novo, ganho: Math.round(((s.ganho ?? 0) + delta) * 10) / 10 };
    });
    return delta ? { ...p, skills, skillTotal: Math.round((p.skillTotal + delta) * 10) / 10 } : p;
  }
  const chaves = STAT[g.id];
  if (!chaves) return p;
  const [efetivo, base] = chaves;
  const delta = g.valor - p.stats[base];
  if (!delta) return p;
  return {
    ...p,
    stats: { ...p.stats, [base]: g.valor, [efetivo]: p.stats[efetivo] + delta, sum: p.stats.sum + delta }
  };
}

/** A dica do valor: quanto falta de XP para o próximo +0,1 (techspec-xp-skills §05.1). Sem XP (coleta, ofício, 100): nada. */
function dicaDoXp(skill: ProfileSkill) {
  const custo = skill.custo ?? 0;
  if (custo <= 0) return {};
  const xp = Math.max(0, Math.min(custo, skill.xp ?? 0));
  return dica({
    titulo: `${nomeDaSkill(skill.id, skill.name)} ${oneDecimal(skill.value)}`,
    texto: t('perfil.skill.xp', { falta: numero(Math.ceil(custo - xp)), pct: numero(Math.floor(xp / custo * 100)) }),
    rodape: t('perfil.skill.xpComo')
  });
}

/** O guia das skills na Wiki, numa aba nova, sem fechar o jogo (como Mais › Wiki). */
function abrirWiki(id: string) {
  window.open(`/wiki/skills#${encodeURIComponent(id)}`, '_blank', 'noopener,noreferrer');
}

/** A dica do nome da skill: o que faz, como sobe e o que o clique faz (techspec-progressao-skills §05.1). */
function dicaDaSkill(skill: ProfileSkill) {
  const faz = `perfil.skill.${skill.id}.faz`;
  const sobe = `perfil.skill.${skill.id}.sobe`;
  return dica({
    titulo: nomeDaSkill(skill.id, skill.name),
    texto: existe(faz) ? t(faz) : nomeDaSkill(skill.id, skill.name),
    ...(existe(sobe) ? { rodape: t(sobe) } : {}),
    acao: t('perfil.skill.acao')
  });
}

export function ProfileGump({ active, vipAte = 0, tick, ganho, canvas, onChanged }: Props) {
  const [sheet, setSheet] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // techspec-pocoes: um efeito de poção começou ou acabou. A ficha relê um instante depois (a sala grava em seguida),
  // para Força, Destreza e Inteligência (e o que sai delas: dano, carga, mana e vigor) virem com o bônus.
  const buffs = useBuffs();
  const [releBuff, setReleBuff] = useState(0);
  const chaveBuffs = buffs.map(b => `${b.tipo}:${b.valor}`).join('|');
  const primeiraChave = useRef(chaveBuffs);
  useEffect(() => {
    if (!active || chaveBuffs === primeiraChave.current) return;
    primeiraChave.current = chaveBuffs;
    const id = window.setTimeout(() => setReleBuff(n => n + 1), 700);
    return () => window.clearTimeout(id);
  }, [active, chaveBuffs]);

  useEffect(() => {
    if (!active) return;
    let live = true;
    api.getProfile()
      .then(next => {
        if (!live) return;
        setSheet(next);
        setError(null);
      })
      .catch(err => {
        if (live) setError(err instanceof ApiError ? err.message : t('perfil.naoAbriu'));
      });
    return () => { live = false; };
  }, [active, tick, releBuff]);

  // Skill, atributo ou barra de XP mudou com o perfil aberto: a ficha se acerta na hora, sem reler. Em fila: uma morte
  // manda vários avisos no mesmo tique.
  const ultimoGanho = useRef(0);
  useEffect(() => {
    if (!ganho || ganho.seq <= ultimoGanho.current) return;
    const novos = ganho.itens.filter(g => g.seq > ultimoGanho.current);
    ultimoGanho.current = ganho.seq;
    if (novos.length) setSheet(cur => (cur ? novos.reduce(aplicarGanho, cur) : cur));
  }, [ganho]);

  const groups = useMemo(() => {
    const map = new Map<string, ProfileSkill[]>();
    for (const skill of sheet?.skills ?? []) {
      const list = map.get(skill.group) ?? [];
      list.push(skill);
      map.set(skill.group, list);
    }
    return GROUP_ORDER
      .map(id => ({ id, skills: map.get(id) ?? [] }))
      .filter(g => g.skills.length > 0);
  }, [sheet]);

  const cycle = async (skill: ProfileSkill) => {
    const next = LOCK_CYCLE[skill.lock];
    setBusy(true);
    setError(null);
    try {
      setSheet(await api.lockSkill(skill.id, next));
      onChanged(lockLine(skill, next));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('perfil.naoAbriu'));
    } finally {
      setBusy(false);
    }
  };

  const [aba, setAba] = useState(false);
  // Limpar o progresso pede confirmação na própria aba (sem diálogo do navegador, que trava o jogo).
  const [confirmaLimpar, setConfirmaLimpar] = useState(false);
  useEffect(() => { if (!aba) setConfirmaLimpar(false); }, [aba]);
  const limparProgresso = async () => {
    setBusy(true);
    setError(null);
    try {
      setSheet(await api.limparProgresso());
      onChanged(t('perfil.progresso.limpo'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('perfil.naoAbriu'));
    } finally {
      setBusy(false);
      setConfirmaLimpar(false);
    }
  };
  const [grupo, setGrupo] = useState<(typeof GROUP_ORDER)[number]>('combate');
  useEffect(() => { if (!active) setAba(false); }, [active]);

  /* o canvas do boneco fica montado desde o começo: o mundo desenha nele quando ele existe */
  if (!sheet) {
    return (
      <>
        <div className="pf-topo">
          <div className="pf-boneco">{canvas}</div>
          <div className="pf-id" />
        </div>
        {error ? <p className="notice" role="alert">{error}</p> : <p className="empty">{t('comum.montando')}</p>}
      </>
    );
  }

  const st = sheet.stats, v = sheet.vitals, c = sheet.combat;
  const dmg = c.damageMin != null && c.damageMax != null ? `${c.damageMin}–${c.damageMax}` : '–';
  const conjuntoBp = setBp(c.conjunto?.familia ?? 'nenhuma', c.conjunto?.completo ?? false);
  const itens = c.itens ?? [];
  // Efeitos que não são atributo (Resistir Magia, Visão noturna) viram tag em Bônus; os de atributo já estão no STR/DEX/INT.
  const efeitos = buffs.filter(b => !ATRIBUTO_DO_BUFF.has(b.tipo) && b.fim > Date.now());
  const fadiga = v.fadiga ?? FADIGA_TETO;
  const lista = groups.find(g => g.id === grupo)?.skills ?? [];
  const ganhoSoma = lista.length ? Math.round(lista.reduce((s, x) => s + (x.ganho ?? 0), 0) * 10) / 10 : undefined;
  const ganhoDoGrupo = textoGanho(ganhoSoma);

  return (
    <>
      <div className="pf-topo">
        <div className="pf-boneco">{canvas}</div>
        <div className="pf-id">
          <div className="pf-cab">
            <p className="pf-quem"><img src={iconeDaProfissao(sheet.profession)} alt="" />{sheet.profession}</p>
            <div className="pf-tags">
              <span className="pf-tag pf-tag--noto" {...rotulo(t('pf.notoriedade'))} style={{ color: nickFillFromLabel(sheet.notoriety) }}>
                <i />{maiuscula(nomeDaNotoriedade(sheet.notoriety))}
              </span>
              {sheet.guildTag ? <span className="pf-tag" {...rotulo(t('pf.guilda'))}>[{sheet.guildTag}]</span> : null}
              {sheet.selo ? <span className="pf-tag"><SeloFundador nivel={sheet.selo} /></span> : null}
              {vipAte > Date.now() && (
                <span className="pf-tag" {...rotulo(t('vip.ateCurto', { data: new Date(vipAte).toLocaleDateString(idiomaAtual()) }))}><CoroaVip /></span>
              )}
            </div>
          </div>
          <div className="pf-attr">
            <Atributo cls="str" rotulo={t('mundo.forca')} ef={st.strength} base={st.strengthBase} buff={st.strengthBuff ?? 0}
              efeito={t('pf.efeitoForca', { carga: numero(sheet.carry.weightMax), peso: stones(sheet.carry.weight) })} />
            <Atributo cls="dex" rotulo={t('mundo.destreza')} ef={st.dexterity} base={st.dexterityBase} buff={st.dexterityBuff ?? 0}
              efeito={t('pf.efeitoDestreza', { vigor: numero(v.stamMax) })} />
            <Atributo cls="int" rotulo={t('mundo.inteligencia')} ef={st.intelligence} base={st.intelligenceBase} buff={st.intelligenceBuff ?? 0}
              efeito={t('pf.efeitoInteligencia', { mana: numero(v.manaMax) })} />
          </div>
        </div>
      </div>
      <div className="pf-vitais">
        {/* Só o valor nos quatro (Renato, 16/10): o "/máx" empurrava a linha; abaixo de 30% fica laranja. */}
        <Recurso icone={<IcVida />} v={v.hits} max={v.hitsMax} nome={t('mundo.vida')} soValor />
        <Recurso icone={<IcMana />} v={v.mana} max={v.manaMax} nome={t('mundo.mana')} soValor />
        <Recurso icone={<IcVigor />} v={v.stam} max={v.stamMax} nome={t('mundo.vigor')} soValor />
        {/* O mesmo ícone do topo; só o valor (o "/1.440" quebrava a linha). Renato, 16/10. */}
        <Recurso icone={<Icone nome="stamina" tam={14} />} v={fadigaPct(fadiga)} max={100} nome={t('mundo.stamina')} soValor sufixo="%" />
      </div>
      <div className="pf-comb">
        <span className="pf-c" {...rotulo(t('perfil.armadura'))}><IcAr />{numero(c.ar)}</span>
        <span className="pf-c" {...rotulo(t('perfil.dano'))}><IcDano />{dmg}</span>
        <span className="pf-c" {...rotulo(t('perfil.velocidade'))}><IcVel />{textoVelocidade(c.velMovimento ?? 1)}</span>
      </div>
      {(conjuntoBp > 0 || itens.length > 0 || efeitos.length > 0) && (
        <>
          <h4 className="ch-sec">{t('perfil.bonus')}</h4>
          <div className="pf-bonus">
            {conjuntoBp > 0 && c.conjunto && (
              <div className="pf-b" {...rotulo(t('perfil.dicaConjunto'))}>
                <b>{c.conjunto.familia === 'nenhuma' ? t('perfil.semArmadura') : t('perfil.conjuntoCompleto', { familia: nomeFamilia(c.conjunto.familia) })}</b>
                <span className="pf-b__ch">
                  <span className="pf-chip"><IcDano />+{conjuntoBp / 100}%</span>
                  <span className="pf-chip"><IcMagia />+{conjuntoBp / 100}%</span>
                </span>
              </div>
            )}
            {itens.map((item, i) => (
              <div key={`${item.nome}-${i}`} className="pf-b">
                <b>{item.nome}</b>
                <span className="pf-b__ch">
                  {item.strBonus ? <Sigla cls="str" n={item.strBonus} /> : null}
                  {item.dexBonus ? <Sigla cls="dex" n={item.dexBonus} /> : null}
                  {item.intBonus ? <Sigla cls="int" n={item.intBonus} /> : null}
                  {item.velAtaqueBp ? <span className="pf-chip"><IcDano />+{item.velAtaqueBp / 100}%</span> : null}
                  {item.velMagiaBp ? <span className="pf-chip"><IcMagia />+{item.velMagiaBp / 100}%</span> : null}
                </span>
              </div>
            ))}
            {efeitos.map(b => <EfeitoDePocao key={b.tipo} buff={b} />)}
          </div>
        </>
      )}
      <button type="button" className="pf-sk" aria-expanded={aba} onClick={() => setAba(x => !x)}>
        <span className="pf-sk__t">{t('perfil.skills')}</span>
        <span className="pf-sk__n">{oneDecimal(sheet.skillTotal)} <small>/ {numero(sheet.skillCap)}</small></span><i>›</i>
      </button>
      {aba && (
        <aside className="gump pf-aba" aria-label={t('perfil.skills')}>
          <div className={`pf-prog${confirmaLimpar ? ' is-confirma' : ''}`}>
            {confirmaLimpar ? (
              <>
                <span className="pf-prog__p">{t('perfil.progresso.confirma')}</span>
                <button type="button" className="pf-prog__b is-sim" disabled={busy} onClick={() => void limparProgresso()}>{t('perfil.progresso.sim')}</button>
                <button type="button" className="pf-prog__b" disabled={busy} onClick={() => setConfirmaLimpar(false)}>{t('perfil.progresso.nao')}</button>
              </>
            ) : (
              <>
                <span className="pf-prog__t" tabIndex={0} {...dica({
                  titulo: t('perfil.progresso.titulo'),
                  texto: t('perfil.progresso.explica'),
                  exemplo: { nome: t('perfil.progresso.exemploSkill'), destaque: '+0,2', valor: '50,3' }
                })}>{t('perfil.progresso.titulo')}</span>
                <button type="button" className="pf-prog__b" {...dica({
                  titulo: t('perfil.progresso.limpar'), texto: t('perfil.progresso.limparDica'), ok: t('perfil.progresso.naoMuda')
                })} disabled={busy} onClick={() => setConfirmaLimpar(true)}>
                  <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8a5 5 0 1 0 1.5-3.5M3 2.5V5h2.5" /></svg>
                  {t('perfil.progresso.limpar')}
                </button>
              </>
            )}
          </div>
          <div className="pf-gr" role="tablist">
            {groups.map(g => (
              <button key={g.id} type="button" role="tab" aria-selected={grupo === g.id} aria-pressed={grupo === g.id} onClick={() => setGrupo(g.id)}>
                {t(GROUP_CHAVE[g.id])}
              </button>
            ))}
          </div>
          <h4 className="ch-sec">
            {t(GROUP_CHAVE[grupo])}
            <span className="ch-sec__dir pf-gr__t">
              {ganhoDoGrupo && <small className={`pf-g${(ganhoSoma ?? 0) < 0 ? ' is-desce' : ''}`}>({ganhoDoGrupo})</small>}
              {oneDecimal(lista.reduce((s, x) => s + x.value, 0))}
            </span>
          </h4>
          <div className="pf-lista">
            {lista.map(skill => {
              const g = textoGanho(skill.ganho);
              return (
              <div key={skill.id} className={`pf-s${skill.value ? '' : ' is-zero'}`}>
                {/* O nome abre a Wiki na linha da skill (techspec-progressao-skills §05.1). */}
                <button type="button" className="pf-s__n" onClick={() => abrirWiki(skill.id)} {...dicaDaSkill(skill)}>
                  {nomeDaSkill(skill.id, skill.name)}
                  {g && (
                    <small className={`pf-g${(skill.ganho ?? 0) < 0 ? ' is-desce' : ''}`} {...rotulo(t('perfil.progresso.ganho', { n: g }))}>({g})</small>
                  )}
                </button>
                <span className="pf-s__v" {...dicaDoXp(skill)}>{oneDecimal(skill.value)}</span>
                <button
                  type="button"
                  className="pf-lock"
                  data-trava={skill.lock}
                  {...dica({
                    titulo: t(LOCK_CHAVE[skill.lock]),
                    texto: t(`perfil.trava.dica.${skill.lock}`, { skill: nomeDaSkill(skill.id, skill.name) }),
                    rodape: t('perfil.trava.ciclo')
                  })}
                  aria-label={t('perfil.skillTrava', { skill: nomeDaSkill(skill.id, skill.name), trava: t(LOCK_CHAVE[skill.lock]) })}
                  disabled={busy}
                  onClick={() => void cycle(skill)}
                >
                  {LOCK_ICON[skill.lock]}
                </button>
                <span className="pf-s__b" role="meter" aria-label={nomeDaSkill(skill.id, skill.name)} aria-valuenow={skill.value} aria-valuemin={0} aria-valuemax={100}>
                  <i style={{ width: `${Math.min(100, skill.value)}%` }} />
                </span>
                {(skill.custo ?? 0) > 0 && (
                  <span className="pf-s__x" aria-hidden="true">
                    <i style={{ width: `${Math.min(100, Math.max(0, (skill.xp ?? 0) / (skill.custo ?? 1) * 100))}%` }} />
                  </span>
                )}
              </div>
              );
            })}
          </div>
          <a className="pf-guia" href="/wiki/skills" target="_blank" rel="noopener noreferrer"
            {...dica({ titulo: t('perfil.guia.titulo'), texto: t('perfil.guia.dica'), acao: t('perfil.guia.acao') })}>
            <span className="pf-guia__ic" aria-hidden="true">
              <svg viewBox="0 0 16 16"><path d="M8 4c-1.4-1-3.4-1.3-5.5-1v9c2.1-.3 4.1 0 5.5 1 1.4-1 3.4-1.3 5.5-1V3C11.4 2.7 9.4 3 8 4zM8 4v9" /></svg>
            </span>
            <span className="pf-guia__t"><b>{t('perfil.guia.titulo')}</b>{t('perfil.guia.sub')}</span>
            <svg className="pf-guia__sai" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3h7v7M13 3 5 11" /></svg>
          </a>
        </aside>
      )}
      {error && <p className="notice" role="alert">{error}</p>}
    </>
  );
}

/** Os tipos de efeito que já aparecem em STR/DEX/INT. */
/** Efeitos que já aparecem no atributo (o de poção e o de magia, o buff e o debuff): não viram tag em Bônus. */
const ATRIBUTO_DO_BUFF = new Set(['forca', 'destreza', 'inteligencia', 'forca-menos', 'destreza-menos', 'inteligencia-menos']);

/**
 * Str/Dex/Int sem ícone: a sigla na cor de cada um, o número grande e o bônus no canto. O bônus de poção
 * (techspec-pocoes) aparece à parte, numa cor própria, para não se confundir com o de item.
 */
function Atributo({ cls, rotulo, ef, base, buff = 0, efeito }: {
  cls: 'str' | 'dex' | 'int'; rotulo: string; ef: number; base: number; buff?: number; efeito: string;
}) {
  const item = ef - base - buff;
  const partes = [item ? `${fmtDelta(item)} ${t('perfil.deItem')}` : '', buff ? `${fmtDelta(buff)} ${t('perfil.dePocao')}` : '']
    .filter(Boolean).join(' ');
  const conta = partes ? `${numero(base)} ${partes}` : '';
  return (
    <span className={`pf-at pf-at--${cls}${buff ? ' com-pocao' : ''}`} aria-label={`${rotulo} ${numero(ef)}`}
      {...dica({ titulo: `${rotulo} ${numero(ef)}`, texto: efeito, ...(conta ? { rodape: `= ${conta}` } : {}) })}>
      <span>{cls}</span><b>{numero(ef)}</b>
      <em>
        {item ? fmtDelta(item) : ''}
        {buff ? <i className="pf-at__pocao">{fmtDelta(buff)}</i> : null}
      </em>
    </span>
  );
}

/** O que falta do efeito: "1:32", "45s". */
function faltaDoEfeito(fim: number): string {
  const s = Math.max(0, Math.ceil((fim - Date.now()) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Uma tag de efeito de poção em Bônus: o ícone da poção, o nome do efeito, o valor e o tempo. */
function EfeitoDePocao({ buff }: { buff: { tipo: string; item: string; valor: number; fim: number } }) {
  const [, setTique] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTique(n => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  const nome = t(`buff.tipo.${buff.tipo}`);
  return (
    <div className="pf-b pf-b--pocao" {...rotulo(t('perfil.efeitoPocao', { item: nomeDoItemPerfil(buff.item) }))}>
      {/* O ícone de buff da UO pelo tipo (techspec-magias-de-efeito); Bless e Curse pela magia. */}
      <b><img src={`/sprites/itens/buffs/${buff.item === 'bless' || buff.item === 'curse' ? buff.item : buff.tipo}.png`} alt="" />{nome}</b>
      <span className="pf-b__ch">
        {buff.valor > 0 ? <span className="pf-chip">{DEBUFFS_PERFIL.has(buff.tipo) ? '−' : '+'}{numero(buff.valor)}</span> : null}
        <span className="pf-chip">{faltaDoEfeito(buff.fim)}</span>
      </span>
    </div>
  );
}

/** A poção ou a magia que deu o efeito. */
function nomeDoItemPerfil(id: string): string {
  if (ehMagia(id)) return nomeDaMagia(id);
  const chave = `item.${id}`;
  const nome = t(chave);
  return nome === chave ? id : nome;
}

const DEBUFFS_PERFIL = new Set(['forca-menos', 'destreza-menos', 'inteligencia-menos']);

function Sigla({ cls, n }: { cls: 'str' | 'dex' | 'int'; n: number }) {
  return <span className="pf-chip">{fmtDelta(n)} <i className={`pf-sig pf-at--${cls}`}>{cls.toUpperCase()}</i></span>;
}

function Recurso({ icone, v, max, nome, soValor = false, sufixo = '' }: { icone: ReactNode; v: number; max: number; nome: string; soValor?: boolean; sufixo?: string }) {
  const baixo = max > 0 && v / max < 0.3;
  const titulo = sufixo === '%'
    ? t('mundo.staminaPct', { n: numero(Math.round(v)) })
    : t('hud.barra.vital', { nome, v: numero(Math.round(v)), max: numero(max) });
  return (
    <span className={`pf-v${baixo ? ' is-baixo' : ''}`} {...rotulo(titulo)}>
      {icone}<b>{numero(Math.round(v))}{sufixo}</b>{!soValor && v < max ? <small>/{numero(Math.round(max))}</small> : null}
    </span>
  );
}

/** O ícone da profissão, da arte do jogo (o nome vem traduzido do servidor). */
function iconeDaProfissao(nome: string): string {
  const n = nome.toLowerCase();
  if (/guerr|warri/.test(n)) return iconePara('large-battle-axe');
  if (/mag/.test(n)) return iconePara('gnarled-staff');
  if (/arque|arch/.test(n)) return iconePara('bow');
  if (/assas/.test(n)) return iconePara('dagger');
  return iconePara('katana');
}

const maiuscula = (s: string) => (s ? s[0].toLocaleUpperCase(idiomaAtual()) + s.slice(1) : s);
